'use client';
import BaseButtonWithProgress from '@components/BaseButtonWithProgress';
import ChevronIcon from '@repo/ui/src/components/Icons/ChevronIcon';
import Image from '@repo/ui/src/components/Image/New';
import Radio from '@components/Radio';
import { Anchor } from '@repo/ui/src/components/Anchor';
import createCurrencyString from '@repo/ui/src/functions/currency-functions';
import { useToggleProductWarranty } from '@hooks/basket/useAddWarrantyToBasket';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { LineItem } from '@repo/ui/src/types/lineItem';
import { LinkProps } from '@repo/ui/src/types/link';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import React from 'react';
import { classNames } from '@repo/ui/src/utils/classNames';
import { ButtonVariant } from '@repo/ui/src/components/Button';
import { ProductOptionValue } from '@repo/ui/src/types/product';
import useAnimatedVisibility from '@hooks/useAnimatedVisibility';

interface Props {
  lineItem: LineItem;
}

const ExtendWarrantyMenu = ({ lineItem }: Props) => {
  const { trigger, isMutating } = useToggleProductWarranty(lineItem);
  const { locale, currency } = useCurrentUrl();
  const { t } = useTranslation();
  const [selectedWarranty, setSelectedWarranty] = React.useState<ProductOptionValue>(
    lineItem.warrantyDefaultOption!,
  );
  const { isVisible, isHidden, toggle } = useAnimatedVisibility();
  const id = React.useId();

  if (!lineItem.warrantyDefaultOption) {
    return null;
  }

  const contentId = `warranty-content-${id}`;
  return (
    <div className={classNames('tw-bg-warm-100 tw-p-4 tw-relative')}>
      <button
        className="tw-w-full tw-grid tw-grid-cols-[auto_minmax(0,1fr)_auto] tw-gap-3 tw-items-center"
        onClick={toggle}
        aria-expanded={isVisible}
        aria-controls={contentId}
      >
        <Image src="/images/shield.png" alt="extend logo" width="24" height="24" />
        <h4 className="tra-text-web-body-l tw-font-bold tw-capitalize tw-text-left tw-mb-0">
          {t('cart.warranty.header', undefined, { default: 'Upgrade Your Protection' })}
        </h4>
        <span className="tw-w-6">
          <ChevronIcon className="tw-w-full" direction={isVisible ? 'up' : 'down'} />
        </span>
      </button>
      <div
        id={contentId}
        className={classNames(
          'tw-overflow-hidden tw-transition-all tw-duration-300 tw-grid tw-gap-y-5',
          isVisible ? 'tw-max-h-screen sm:tw-max-h-animate tw-pt-5' : 'tw-max-h-0',
          isHidden && 'tw-hidden',
        )}
        aria-hidden={!isVisible}
      >
        <fieldset
          onChange={(e) => {
            const warranty = lineItem.warrantyOptions?.find(
              (item) => item?.id === (e.target as HTMLInputElement)?.value,
            );
            setSelectedWarranty(warranty ?? lineItem.warrantyDefaultOption!);
          }}
          className="tw-grid tw-gap-y-4"
        >
          <legend className="sr-only">
            {t('cart.warranty.header', undefined, { default: 'Upgrade Your Protection' })}
          </legend>
          {lineItem.warrantyOptions?.map((plan) => (
            <label
              key={plan?.id}
              className="tw-grid tw-grid-cols-[auto_2fr_1fr] tw-gap-x-3 tw-items-center"
            >
              <Radio name={id} value={plan?.id} checked={selectedWarranty?.id === plan?.id} />
              <p className="tra-text-web-body-l tw-mb-0">{plan.name}</p>
              <p className="tra-text-web-body-l tw-mb-0 tw-justify-self-end">
                {plan === lineItem.warrantyDefaultOption
                  ? t('addToCartWindowContent.shippingFreeLabel', undefined, {
                      default: 'Free',
                    })
                  : `+ ${createCurrencyString(locale, currency, plan.price)}`}
              </p>
            </label>
          ))}
        </fieldset>
        <div className="tw-grid tw-gap-y-4">
          <BaseButtonWithProgress
            onClick={() => {
              if (selectedWarranty) {
                trigger(selectedWarranty);
              }
            }}
            disabled={selectedWarranty?.id === lineItem.warrantyDefaultOption!.id}
            variant={ButtonVariant.secondary}
            className="tw-w-full tw-rounded"
            processing={isMutating}
            processingText={false}
            data-testid="add-warranty-btn"
          >
            {t('cart.warranty.buttonText', undefined, { default: 'Add Extended Protection' })}
          </BaseButtonWithProgress>
          {lineItem.warrantyPlanId && (
            <div className="tw-grid tw-justify-center">
              <Anchor
                {...t(
                  'cart.warranty.detailsLink',
                  {
                    planId: lineItem.warrantyPlanId,
                  },
                  {
                    default: {
                      displayName: 'Extend Details & FAQs',
                      externalLink: `https://customers.extend.com/en-US/plan_details/${lineItem.warrantyPlanId}`,
                      isExternal: true,
                    } as LinkProps,
                    returnObjects: true,
                  },
                )}
                className="tw-underline"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExtendWarrantyMenu;
